import React, {useState, useMemo} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
//local
import {Layout} from "components/UI/layout";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {IconFeather, IconPlus} from "components/icons";
import {Tabs} from "components/Tabs";
import {signContractsTabs, defaultSignContractsParamsSearch} from "./constants";
import {toSignContractId} from "constants/routes.const";
import {createSignContract} from "./api/contracts";
import {SignContractsContext} from "./SignContractsContext";
import {
  hasPermission,
  hasValue,
  getUrlParams,
  hasPermissionQuantity,
} from "helpers/helper";
import {color} from "constants/color.consts";
import {getSessionStorage} from "helpers/storage";
import "./styles/table.scss";

const SignContractsPage = inject("store")(
  observer(({store: {auth}}) => {
    const {user} = auth;
    const navigate = useNavigate();
    const location = useLocation();
    const {t} = useTranslation();

    const [activeTab, setActiveTab] = useState(
      signContractsTabs.find(
        (tab) =>
          tab.requestKey === Object.fromEntries(new URLSearchParams(location.search))?.tab
      )?.id ?? user.countOfUnsingedContractsSentToMe
        ? 1
        : 0
    );
    const [createLoading, setCreateLoading] = useState(false);

    const canCreate = hasPermission(["CREATE_SIGN_CONTRACT"]);
    const quantityCreatedContracts = useMemo(
      () =>
        user?.permissions.find(
          (permission) => permission.PermissionId === "CREATE_SIGN_CONTRACT"
        )?.quantity,
      [user]
    );
    const hasQuantityCreatedContracts = hasValue(quantityCreatedContracts);

    const createdContractsMaxCount = useMemo(
      () => quantityCreatedContracts ?? 0,
      [quantityCreatedContracts]
    );
    const createdContractsCount = useMemo(
      () => user.signContractsCreatedCount ?? 0,
      [user]
    );
    const canFreeCreateContract = useMemo(
      () =>
        hasQuantityCreatedContracts && createdContractsCount < createdContractsMaxCount,
      [hasQuantityCreatedContracts, createdContractsCount, createdContractsMaxCount]
    );
    const remainingContractsCount = useMemo(
      () => Math.max(0, createdContractsMaxCount - createdContractsCount),
      [createdContractsMaxCount, createdContractsCount]
    );
    const showQuantityExplanation = useMemo(
      () => !canCreate || hasQuantityCreatedContracts,
      [canCreate, hasQuantityCreatedContracts]
    );

    const clickCreateContract = () => {
      if (!(canCreate && (!hasQuantityCreatedContracts || canFreeCreateContract))) return;
      setCreateLoading(true);
      createSignContract()
        .then((data) => {
          hasPermissionQuantity("CREATE_SIGN_CONTRACT") && auth.checkToken(true);
          navigate(toSignContractId(data.id));
        })
        .finally(() => setCreateLoading(false));
    };

    const handleChangeTabUrlParams = (tab) => {
      const storageParams = JSON.parse(
        getSessionStorage(`filterParamsForSignContracts-${tab}`)
      );
      const params = storageParams ?? {
        ...defaultSignContractsParamsSearch,
        tab: signContractsTabs.find((t) => t.id === tab).requestKey,
      };
      navigate({search: getUrlParams(params), replace: true});
    };

    return (
      <SignContractsContext.Provider
        value={{
          signContractsActiveTab: activeTab,
          setSignContractsActiveTab: setActiveTab,
          currentUser: user,
        }}
      >
        <Layout>
          <div className="signing-contracts">
            <div className="content-block_header">
              <IconFeather color={color.black} addClass="ml-2" />
              <h3 className="content-block_title __uppercase">
                {t("SIGN_CONTRACTS_TITLE")}
              </h3>
              {showQuantityExplanation && (
                <div className="signing-contracts_head__counter">
                  <span className="__caveat fz-20">
                    {t("SIGN_CONTRACTS_CREATE_CONTRACT_DESCRIPTION")}
                  </span>
                  <div>
                    {t("SIGN_CONTRACTS_CREATE_CONTRACT_COUNTER").format(
                      remainingContractsCount,
                      createdContractsMaxCount
                    )}
                  </div>
                </div>
              )}

              <ButtonTransparent
                addClass={`signing-contracts_head__btn${
                  showQuantityExplanation ? "" : "  mla"
                }`}
                name="SIGN_CONTRACTS_CREATE_CONTRACT"
                icon={<IconPlus />}
                loading={createLoading}
                disabled={!(canCreate || canFreeCreateContract)}
                onClick={clickCreateContract}
              />
            </div>
            <Tabs
              tabs={signContractsTabs}
              active={activeTab}
              setActive={(tab) => {
                handleChangeTabUrlParams(tab);
                activeTab !== tab && setActiveTab(tab);
              }}
              addClass="signing-contracts_content"
            />
          </div>
        </Layout>
      </SignContractsContext.Provider>
    );
  })
);

export default SignContractsPage;
